import { t } from 'i18next';
import { useMemo } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { useAssetsByWorkspaceQuery } from 'queries/assets/queries';
import { BaseAsset } from 'queries/assets/contract';
import { ASSET_PAGE, HOME_PATH } from 'config/paths';
import { findByIdRecursive } from 'utils/Trail';

type TBreadcrumb = {
  name: string;
  onClick?: (() => void) | undefined;
};
export const pagesWithBreadcrumb = ['monitoring', 'overview', 'model'];

export const getBreadcrumbByPathName = (pathname: string): TBreadcrumb[] => {
  switch (pathname) {
    case HOME_PATH:
      return [
        {
          name: t('glossary:home'),
        },
      ];
    case '/users-and-permissions/users-and-roles':
    case '/users-and-permissions/access-levels':
    case '/users-and-permissions/notifications': {
      return [
        {
          name: t('glossary:users_and_permissions'),
        },
      ];
    }

    default: {
      return [];
    }
  }
};
export const getBreadcrumbByAssetId = (
  assetId: string,
  tree: BaseAsset[],
  navigate: NavigateFunction,
): TBreadcrumb[] => {
  const tempTrail = findByIdRecursive(tree, String(assetId));

  if (tempTrail?.breadcrumbPath) {
    return tempTrail?.breadcrumbPath
      .map((item) => ({
        name: item.name,
        onClick: () => navigate(ASSET_PAGE(item.id)),
      }))
      .reverse();
  }
  return [];
};
export const useBreadcrumb = (assetId?: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useAssetsByWorkspaceQuery();

  const breadcrumbByPathName = useMemo(
    () => getBreadcrumbByPathName(location.pathname),
    [location.pathname],
  );

  const breadcrumbByAssetId = useMemo(() => {
    if (data) {
      const tree = data.items;
      const breadcrumbByAssetId = getBreadcrumbByAssetId(
        assetId ?? data.items[0].id,
        tree,
        navigate,
      );
      return breadcrumbByAssetId;
    }
    return [];
  }, [data, assetId, navigate]);

  const isPageWithoutBreadcrumb = !pagesWithBreadcrumb.some((page) => {
    return location.pathname.includes(page);
  });

  // Verifica se é exatamente a página do asset sem subrotas
  const isAssetPageWithoutSubroutes = location.pathname.includes('/asset');
  if (isPageWithoutBreadcrumb && !isAssetPageWithoutSubroutes) {
    return breadcrumbByAssetId[0] ? [breadcrumbByAssetId[0]] : [];
  }
  return [...breadcrumbByPathName, ...breadcrumbByAssetId];
};
